import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Smartphone, Mail, Ban, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SettingsPanel, PanelHeader, EmptyState, LoadingState, TABLE_HEAD_CLASS } from "@/components/settings/SettingsUI";
import { listFieldUsers, resendFieldInvite, revokeFieldUser, changeFieldRole,
         revokeFieldSession, getFieldAuthFlags, putFieldAuthFlags, listAccessRequests, decideAccessRequest } from "@/lib/api";
import InviteFieldUserDialog from "./InviteFieldUserDialog";

export const FIELD_ROLES = [{ value: "technician", label: "Technician" }];
const STATUS = { invited: "bg-sky-50 text-sky-700", active: "bg-emerald-50 text-emerald-700", revoked: "bg-rose-50 text-rose-700" };
const fmt = (s) => (s ? String(s).replace("T", " ").slice(0, 16) : "—");
// iter309 — what the last sign-in-link email actually did. "Accepted"
// is deliberate: Resend took it, delivery is only proven by a webhook
// confirmation ("Delivered").
const EMAIL_STATUS_LABEL = {
  sent: "Accepted by Resend",
  delivered: "Delivered",
  bounced: "Bounced",
  complained: "Marked as spam",
  delayed: "Delayed",
  error: "Send failed",
  skipped_feature_disabled: "Not sent — automation OFF",
  skipped_no_api_key: "Not sent — Resend not configured",
  skipped_test_domain: "Not sent — test domain",
};
const EMAIL_STATUS_STYLE = {
  sent: "text-sky-700",
  delivered: "text-emerald-700 font-medium",
  bounced: "text-rose-700 font-medium",
  complained: "text-rose-700 font-medium",
  delayed: "text-amber-700",
  error: "text-rose-700 font-medium",
  skipped_feature_disabled: "text-amber-700 font-medium",
  skipped_no_api_key: "text-rose-700 font-medium",
  skipped_test_domain: "text-amber-700",
};
const errMsg = (e) => e?.response?.data?.detail || e?.message || "Failed";

function Flag({ id, label, hint, value, onChange, disabled }) {
  return (
    <label className={`flex items-start gap-2 text-xs ${disabled ? "opacity-60" : "cursor-pointer"}`} data-testid={`field-flag-${id}`}>
      <input type="checkbox" checked={!!value} disabled={disabled} onChange={(e) => onChange(e.target.checked)} className="mt-0.5 h-4 w-4 accent-slate-900" data-testid={`field-flag-toggle-${id}`} />
      <span><span className={`font-semibold ${value ? "text-emerald-700" : "text-slate-700"}`}>{label}: {value ? "ON" : "OFF"}</span><br /><span className="text-slate-500">{hint}</span></span>
    </label>
  );
}

export default function FieldUsersPanel() {
  const location = useLocation();
  const [users, setUsers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [flags, setFlags] = useState({ magic_link_enabled: false, request_to_join_enabled: false });
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [u, f, r] = await Promise.all([listFieldUsers(), getFieldAuthFlags(), listAccessRequests()]);
      // Revoked field users live under the Archived sub-tab (reinstate from there).
      setUsers(u.data.filter((x) => x.field_status !== "revoked")); setFlags(f.data); setRequests(r.data.filter((x) => x.status === "pending"));
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  // Approve / Deny links from the admin email land here with ?access_request=<id>&decision=…
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    const id = p.get("access_request"), decision = p.get("decision");
    if (id && decision && !loading) {
      const req = requests.find((r) => r.id === id);
      if (req && window.confirm(`${decision === "approve" ? "Approve" : "Deny"} field access for ${req.name} <${req.email}>?`)) {
        act(() => decideAccessRequest(id, decision), `Request ${decision}d`);
      }
      window.history.replaceState({}, "", "/settings?tab=users");
    }
  }, [location.search, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (fn, ok) => {
    try { await fn(); if (ok) toast.success(ok); reload(); } catch (e) { toast.error(errMsg(e)); }
  };

  // iter309 — never claim an email was sent. Every link/invite endpoint
  // returns the real `email_status` from the send path; a skip (automation
  // off, no API key) or an error has to be loud, not a green toast.
  const actEmail = async (fn, sentMsg) => {
    try {
      const { data } = await fn();
      const status = data?.email_status;
      const to = data?.to || "";
      if (status === "sent") {
        toast.success(`${sentMsg}${to ? ` to ${to}` : ""}`);
      } else if (status === "skipped") {
        toast.error("Email NOT sent", {
          description: data?.email_reason === "no_api_key"
            ? "Resend is not configured on the server (RESEND_API_KEY missing)."
            : data?.email_reason === "reserved_test_tld"
              ? "That address uses a reserved test domain and can never receive mail."
              : "Email automation is OFF — turn it on in Settings → General → Email Settings.",
          duration: 10000,
        });
      } else {
        toast.error(`Email failed${to ? ` for ${to}` : ""}`, {
          description: data?.email_error || "Resend rejected the message. See Settings → Email Settings.",
          duration: 10000,
        });
      }
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const setFlag = (k, v) => act(() => putFieldAuthFlags({ [k]: v }), null);

  return (
    <SettingsPanel>
      <PanelHeader icon={Smartphone} title="Field access (magic link)" count={users.length || null}
                   hint="Passwordless sign-in for Technicians who are not in JumpCloud. Lead Technicians sign in with JumpCloud."
                   actions={
                     <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
                       <DialogTrigger asChild><Button size="sm" data-testid="btn-invite-field-user"><Mail className="w-3.5 h-3.5 mr-1.5" /> Invite Field User</Button></DialogTrigger>
                       <InviteFieldUserDialog onDone={() => { setInviteOpen(false); reload(); }} onCancel={() => setInviteOpen(false)} />
                     </Dialog>
                   } />
      <div className="rounded-md border border-slate-200 bg-slate-50 p-3 grid md:grid-cols-2 gap-3" data-testid="field-flags">
        <Flag id="magic_link_enabled" label="Magic-link sign-in" value={flags.magic_link_enabled} onChange={(v) => setFlag("magic_link_enabled", v)}
              hint="Master switch. While OFF the login page hides the field option and every link request / link is refused." />
        <Flag id="request_to_join_enabled" label="Request Access Form" value={flags.request_to_join_enabled} onChange={(v) => setFlag("request_to_join_enabled", v)}
              hint="Optional. Lets unknown people ask for access; admins approve by email link (admin session required)." />
      </div>

      {requests.length > 0 && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-2 space-y-1" data-testid="field-access-requests">
          <div className="text-xs font-semibold text-amber-900">Pending access requests</div>
          {requests.map((r) => (
            <div key={r.id} className="flex items-center gap-2 text-xs" data-testid={`access-request-${r.id}`}>
              <span className="font-medium">{r.name}</span><span className="font-mono text-slate-600">{r.email}</span><span className="text-slate-400">{fmt(r.requested_at)}</span>
              <Button size="sm" className="h-6 text-[11px] bg-emerald-600 hover:bg-emerald-700 ml-auto" onClick={() => actEmail(() => decideAccessRequest(r.id, "approve"), "Approved — invite emailed")} data-testid={`access-approve-${r.id}`}>Approve</Button>
              <Button size="sm" variant="outline" className="h-6 text-[11px] text-rose-700" onClick={() => act(() => decideAccessRequest(r.id, "deny"), "Denied")} data-testid={`access-deny-${r.id}`}>Deny</Button>
            </div>
          ))}
        </div>
      )}

      {loading ? <LoadingState testId="field-users-loading" /> : users.length === 0 ? (
        <EmptyState icon={Smartphone} title="No field users yet" hint="Invite a technician — they get a sign-in link by email." testId="field-users-empty" />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="field-users-table">
            <thead className={TABLE_HEAD_CLASS}><tr>
              <th className="px-3 py-2 text-left">Name</th><th className="px-3 py-2 text-left">Status</th><th className="px-3 py-2 text-left">Role</th>
              <th className="px-3 py-2 text-left">Roster</th><th className="px-3 py-2 text-left">Last Link Email</th><th className="px-3 py-2 text-left">Last Login</th><th className="px-3 py-2 text-left">Devices</th>
              <th className="px-3 py-2 text-left">Token Expiry</th><th className="px-3 py-2 text-right">Actions</th>
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {users.map((u) => (
                <tr key={u.id} data-testid={`field-user-row-${u.id}`}>
                  <td className="px-3 py-2"><div className="font-medium text-slate-800">{u.name}</div><div className="font-mono text-[11px] text-slate-500">{u.email}</div></td>
                  <td className="px-3 py-2"><Badge variant="outline" className={`border-0 text-[10px] uppercase ${STATUS[u.field_status] || ""}`} data-testid={`field-status-${u.id}`}>{u.field_status}</Badge></td>
                  <td className="px-3 py-2">
                    <Select value={u.role} onValueChange={(v) => act(() => changeFieldRole(u.id, v), `Role → ${v}. Devices signed out.`)}>
                      <SelectTrigger className="h-7 text-xs w-40" data-testid={`field-role-${u.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{FIELD_ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                    </Select>
                    {u.role === "lead_technician" && (
                      <div className="text-[11px] text-rose-700 mt-1" data-testid={`field-lead-warning-${u.id}`}>
                        Lead Techs sign in with JumpCloud — switch to Technician or revoke this field user.
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs" data-testid={`field-roster-${u.id}`}>
                    {u.roster?.linked ? <span className="text-emerald-700">Linked{u.roster.is_lead ? " · lead" : ""}</span> : <span className="text-rose-700 font-medium">Not linked</span>}
                  </td>
                  <td className="px-3 py-2 text-xs" data-testid={`field-last-email-${u.id}`}>
                    {u.last_link_email ? (
                      <>
                        <span className={EMAIL_STATUS_STYLE[u.last_link_email.status] || "text-slate-600"}>
                          {EMAIL_STATUS_LABEL[u.last_link_email.status] || u.last_link_email.status}
                        </span>
                        <div className="text-[10px] text-slate-400">{fmt(u.last_link_email.created_at)}</div>
                      </>
                    ) : <span className="text-slate-400">Never</span>}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-600">{fmt(u.last_login_at)}</td>
                  <td className="px-3 py-2 text-xs" data-testid={`field-devices-${u.id}`}>
                    <div>{u.device_count} / 3</div>
                    {u.sessions.map((s) => (
                      <div key={s.id} className="flex items-center gap-1 text-[11px] text-slate-500" data-testid={`field-session-${s.id}`}>
                        <span className="truncate max-w-[160px]" title={s.device}>{s.device}</span>
                        <button type="button" className="text-rose-600 underline" onClick={() => act(() => revokeFieldSession(u.id, s.id), "Device signed out")} data-testid={`field-session-revoke-${s.id}`}>sign out</button>
                      </div>
                    ))}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-600" data-testid={`field-expiry-${u.id}`}>{fmt(u.token_expiry)}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => actEmail(() => resendFieldInvite(u.id), "Sign-in link emailed")} data-testid={`field-resend-${u.id}`}><Send className="w-3.5 h-3.5 mr-1" />Resend Link</Button>
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-amber-700" title="Revoke — signs out every device and moves them to Archived; reinstate any time" onClick={() => window.confirm(`Revoke access for ${u.email}?\n\nAll devices are signed out immediately. They move to the Archived tab and can be reinstated any time.`) && act(() => revokeFieldUser(u.id), "Access revoked — find them under Archived")} data-testid={`field-revoke-${u.id}`}><Ban className="w-3.5 h-3.5 mr-1" />Revoke</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </SettingsPanel>
  );
}
